import { format } from "date-fns";

import type { SessionMetricPoint } from "api/types/charts";

// ---------------------------------------------------------------------------
// Metric → Chart registry
//
// Single source of truth mapping each ToggleChartGroup metric (keyed by its
// canonical title from ANALYTICS_BY_L1_L3, which is also the toggle option
// `label`) to the chart that renders it: the chart `kind`, the
// `SessionMetricPoint` field that supplies the data, and the display copy.
//
// `kind` is a discriminator the consumer switches on to pick the chart
// component (and its y-axis scaling). New chart categories are added by
// extending `MetricChartConfig` with another variant and registering entries
// here — the rendering layer and the data hooks stay untouched.
// ---------------------------------------------------------------------------

/** `SessionMetricPoint` fields rendered as a 0–100% line series. */
export type PercentMetricField =
  | "percent_independent"
  | "percent_correct"
  | "percent_steps_independent"
  | "percent_steps_correct"
  | "percent_of_opportunities"
  | "percent_session_in_behavior"
  | "percent_trials_meeting_duration_criterion";

/** `SessionMetricPoint` fields rendered as an auto-scaled count/rate line. */
export type CountRateMetricField =
  "count_per_session" | "bout_count" | "rate_per_hour" | "opportunity_frequency";

/** `SessionMetricPoint` duration fields (stored in ms) rendered as a line. */
export type DurationMetricField = "mean_duration_ms" | "total_duration_ms" | "longest_bout_ms";

/** `SessionMetricPoint` latency fields (stored in ms) rendered as a line. */
export type LatencyMetricField = "mean_latency_ms";

/** `SessionMetricPoint` rating-scale fields rendered as a line. */
export type RatingMetricField = "mean_rating" | "rolling_average_rating";

/**
 * `SessionMetricPoint` fields that hold a per-session breakdown keyed by
 * category code (prompt level), aggregated across sessions into a distribution
 * bar chart rather than a line.
 */
export type RecordMetricField = "prompt_level_distribution" | "latency_by_prompt_level";

/** Any numeric `SessionMetricPoint` field usable as a line-chart y-value. */
export type LineMetricField =
  | PercentMetricField
  | CountRateMetricField
  | DurationMetricField
  | LatencyMetricField
  | RatingMetricField;

/** Converts a millisecond value to seconds — the unit time-based charts display. */
export const msToSeconds = (ms: number): number => ms / 1000;

/**
 * "Percentage Line Charts" category — single metric over sessions, y-axis
 * fixed 0–100%, with a mastery criterion line, rendered by `TargetChart`.
 * (Figma: node 5210-11430.)
 */
export interface PercentageLineChartConfig {
  kind: "percentage-line";
  /** Canonical metric title — matches the ToggleChartGroup option label. */
  title: string;
  /** `SessionMetricPoint` field supplying the y-value per session. */
  field: PercentMetricField;
  /** Descriptive header shown above the chart. */
  chartTitle: string;
  /** Y-axis label. */
  yAxisLabel: string;
}

/**
 * "Count & Rate Line Charts" category — discrete counts or rates over
 * sessions, auto-scaled y-axis (no fixed 0–100, no mastery line), rendered by
 * `TargetChart` with `yScale="auto"`. (Figma: node 5211-12041.)
 */
export interface CountRateLineChartConfig {
  kind: "count-rate-line";
  /** Canonical metric title — matches the ToggleChartGroup option label. */
  title: string;
  /** `SessionMetricPoint` field supplying the y-value per session. */
  field: CountRateMetricField;
  /** Descriptive header shown above the chart. */
  chartTitle: string;
  /** Y-axis label. */
  yAxisLabel: string;
}

/**
 * "Duration Line Charts" category — time-based values over sessions, auto-scaled
 * y-axis (no fixed 0–100, no mastery line), rendered by `TargetChart` with
 * `yScale="auto"`. Values are stored in milliseconds and charted in seconds via
 * `transform`. (Figma: node 5224-5248.)
 */
export interface DurationLineChartConfig {
  kind: "duration-line";
  /** Canonical metric title — matches the ToggleChartGroup option label. */
  title: string;
  /** `SessionMetricPoint` field supplying the y-value per session (ms). */
  field: DurationMetricField;
  /** Converts the stored value to the charted unit (ms → seconds). */
  transform: (value: number) => number;
  /** Descriptive header shown above the chart. */
  chartTitle: string;
  /** Y-axis label (includes the unit). */
  yAxisLabel: string;
}

/**
 * "Latency Line Charts" category — response-time values over sessions. Renders
 * exactly like `duration-line` (auto-scaled y-axis, ms → seconds, no mastery
 * line); kept as its own kind to mirror the design's category and allow latency
 * to diverge later (e.g. multi-series from `latency_by_prompt_level`).
 * (Figma: node 5225-4561.)
 */
export interface LatencyLineChartConfig {
  kind: "latency-line";
  /** Canonical metric title — matches the ToggleChartGroup option label. */
  title: string;
  /** `SessionMetricPoint` field supplying the y-value per session (ms). */
  field: LatencyMetricField;
  /** Converts the stored value to the charted unit (ms → seconds). */
  transform: (value: number) => number;
  /** Descriptive header shown above the chart. */
  chartTitle: string;
  /** Y-axis label (includes the unit). */
  yAxisLabel: string;
}

/**
 * "Rating Line Charts" category — scale-based ratings (0-5) over sessions,
 * auto-scaled y-axis (no fixed bounds, no mastery line, no transform), rendered
 * by `TargetChart` with `yScale="auto"`. The `rating_scale` primitive exposes a
 * single `mean_rating` field; the differing metric labels are the same field
 * under different L3 contexts. (Figma: node 5225-5291.)
 */
export interface RatingLineChartConfig {
  kind: "rating-line";
  /** Canonical metric title — matches the ToggleChartGroup option label. */
  title: string;
  /** `SessionMetricPoint` field supplying the y-value per session. */
  field: RatingMetricField;
  /** Descriptive header shown above the chart. */
  chartTitle: string;
  /** Y-axis label. */
  yAxisLabel: string;
}

/**
 * "Distribution Charts" category — a categorical breakdown (one bar per prompt
 * level), rendered by `TargetDistributionChart`. The backing field is a
 * per-session `Record<code, number>`; the values are combined across sessions
 * per code (`aggregate`) and optionally transformed (e.g. ms→s for latency).
 * (Figma: node 5228-6520.)
 */
export interface DistributionChartConfig {
  kind: "distribution";
  /** Canonical metric title — matches the ToggleChartGroup option label. */
  title: string;
  /** `SessionMetricPoint` record field supplying the per-session breakdown. */
  field: RecordMetricField;
  /** How to combine each category's per-session values across the target. */
  aggregate: "sum" | "mean";
  /** Optional per-value transform applied before aggregation (e.g. ms→s). */
  transform?: (value: number) => number;
  /** Descriptive header shown above the chart. */
  chartTitle: string;
  /** X-axis label. */
  xAxisLabel: string;
}

/** Configs rendered as a session line chart (via `TargetMetricChart`). */
export type LineChartConfig =
  | PercentageLineChartConfig
  | CountRateLineChartConfig
  | DurationLineChartConfig
  | LatencyLineChartConfig
  | RatingLineChartConfig;

/** Any chart config supported by the metric registry. */
export type MetricChartConfig = LineChartConfig | DistributionChartConfig;

/**
 * Registry keyed by canonical metric title. Entries are limited to labels with
 * backing fields in the `session-metrics` payload. A few design labels remain
 * intentionally unsupported because no API field exists (or the L-model combo
 * is invalid):
 * "Mean Step Duration", "Total Chain Duration", and "Mean Inter-Step Latency".
 */
export const METRIC_CHART_REGISTRY: Record<string, MetricChartConfig> = {
  // --- Percentage Line Charts (y-axis 0–100%, mastery line) ---
  "% Independent": {
    kind: "percentage-line",
    title: "% Independent",
    field: "percent_independent",
    chartTitle: "Trials/opportunities completed without prompting",
    yAxisLabel: "% Independent",
  },
  "% Correct": {
    kind: "percentage-line",
    title: "% Correct",
    field: "percent_correct",
    chartTitle: "Trials with correct responses",
    yAxisLabel: "% Correct",
  },
  "% Steps Independent": {
    kind: "percentage-line",
    title: "% Steps Independent",
    field: "percent_steps_independent",
    chartTitle: "Chain steps completed independently",
    yAxisLabel: "% Steps Independent",
  },
  "% Steps Correct": {
    kind: "percentage-line",
    title: "% Steps Correct",
    field: "percent_steps_correct",
    chartTitle: "Chain steps performed correctly",
    yAxisLabel: "% Steps Correct",
  },

  // --- Count & Rate Line Charts (auto-scaled y-axis, no mastery line) ---
  "Count per Session": {
    kind: "count-rate-line",
    title: "Count per Session",
    field: "count_per_session",
    chartTitle: "Number of occurrences each session",
    yAxisLabel: "Count per Session",
  },
  "Bout Count": {
    kind: "count-rate-line",
    title: "Bout Count",
    field: "bout_count",
    chartTitle: "Number of behavior episodes per session",
    yAxisLabel: "Bout Count",
  },
  "Rate (per hour)": {
    kind: "count-rate-line",
    title: "Rate (per hour)",
    field: "rate_per_hour",
    chartTitle: "Occurrences divided by session time",
    yAxisLabel: "Rate (per hour)",
  },
  // Backward-compatible alias for existing saved labels.
  "Rate (Per Hour)": {
    kind: "count-rate-line",
    title: "Rate (Per Hour)",
    field: "rate_per_hour",
    chartTitle: "Occurrences divided by session time",
    yAxisLabel: "Rate (per hour)",
  },
  "Opportunity Frequency": {
    kind: "count-rate-line",
    title: "Opportunity Frequency",
    field: "opportunity_frequency",
    chartTitle: "How often opportunities occurred",
    yAxisLabel: "Opportunity Frequency",
  },

  // --- Duration Line Charts (auto-scaled y-axis, ms → seconds, no mastery) ---
  "Mean Duration": {
    kind: "duration-line",
    title: "Mean Duration",
    field: "mean_duration_ms",
    transform: msToSeconds,
    chartTitle: "Average length of each bout",
    yAxisLabel: "Mean Duration (s)",
  },
  "Total Duration": {
    kind: "duration-line",
    title: "Total Duration",
    field: "total_duration_ms",
    transform: msToSeconds,
    chartTitle: "Cumulative duration across session",
    yAxisLabel: "Total Duration (s)",
  },
  "Mean Duration Per Trial": {
    kind: "duration-line",
    title: "Mean Duration Per Trial",
    field: "mean_duration_ms",
    transform: msToSeconds,
    chartTitle: "Average duration of behavior within each trial",
    yAxisLabel: "Mean Duration (s)",
  },
  "Mean Duration Per Opportunity": {
    kind: "duration-line",
    title: "Mean Duration Per Opportunity",
    field: "mean_duration_ms",
    transform: msToSeconds,
    chartTitle: "Average duration within each natural opportunity",
    yAxisLabel: "Mean Duration (s)",
  },
  "Total Duration Per Session": {
    kind: "duration-line",
    title: "Total Duration Per Session",
    field: "total_duration_ms",
    transform: msToSeconds,
    chartTitle: "Cumulative duration across session",
    yAxisLabel: "Total Duration (s)",
  },
  "Longest Bout": {
    kind: "duration-line",
    title: "Longest Bout",
    field: "longest_bout_ms",
    transform: msToSeconds,
    chartTitle: "Maximum bout length per session",
    yAxisLabel: "Longest Bout (s)",
  },

  // --- Latency Line Charts (auto-scaled y-axis, ms → seconds, no mastery) ---
  "Mean Latency": {
    kind: "latency-line",
    title: "Mean Latency",
    field: "mean_latency_ms",
    transform: msToSeconds,
    chartTitle: "Average time from SD/opportunity to response",
    yAxisLabel: "Mean Latency (s)",
  },
  "Mean Response Latency": {
    kind: "latency-line",
    title: "Mean Response Latency",
    field: "mean_latency_ms",
    transform: msToSeconds,
    chartTitle: "Average time from SD/opportunity to response",
    yAxisLabel: "Mean Response Latency (s)",
  },
  "Mean Latency To Naturally Occurring Response": {
    kind: "latency-line",
    title: "Mean Latency To Naturally Occurring Response",
    field: "mean_latency_ms",
    transform: msToSeconds,
    chartTitle: "Average latency to naturally occurring response",
    yAxisLabel: "Mean Latency (s)",
  },
  // Backward-compatible alias.
  "Mean Latency to Response": {
    kind: "latency-line",
    title: "Mean Latency to Response",
    field: "mean_latency_ms",
    transform: msToSeconds,
    chartTitle: "Average time from opportunity to response",
    yAxisLabel: "Mean Latency (s)",
  },

  // --- Rating Line Charts (auto-scaled y-axis, no transform, no mastery) ---
  // All read `mean_rating`; the labels differ only by L3 context.
  "Mean Trial Rating": {
    kind: "rating-line",
    title: "Mean Trial Rating",
    field: "mean_rating",
    chartTitle: "Average quality/intensity rating per trial",
    yAxisLabel: "Mean Trial Rating",
  },
  "Mean Step Quality Rating": {
    kind: "rating-line",
    title: "Mean Step Quality Rating",
    field: "mean_rating",
    chartTitle: "Average quality rating across chain steps",
    yAxisLabel: "Mean Step Quality Rating",
  },
  "Mean Naturalistic Rating": {
    kind: "rating-line",
    title: "Mean Naturalistic Rating",
    field: "mean_rating",
    chartTitle: "Average rating of naturally occurring behavior",
    yAxisLabel: "Mean Naturalistic Rating",
  },
  "Daily Performance Rating": {
    kind: "rating-line",
    title: "Daily Performance Rating",
    field: "mean_rating",
    chartTitle: "Average performance rating per session",
    yAxisLabel: "Daily Performance Rating",
  },
  "Rolling Average": {
    kind: "rating-line",
    title: "Rolling Average",
    field: "rolling_average_rating",
    chartTitle: "Smoothed average over recent sessions",
    yAxisLabel: "Rolling Average",
  },

  // --- Additional percentage lines ---
  "% Of Opportunities": {
    kind: "percentage-line",
    title: "% Of Opportunities",
    field: "percent_of_opportunities",
    chartTitle: "Percentage of opportunities with target response",
    yAxisLabel: "% Of Opportunities",
  },
  "% Of Session In Behavior": {
    kind: "percentage-line",
    title: "% Of Session In Behavior",
    field: "percent_session_in_behavior",
    chartTitle: "Portion of observed session time in behavior",
    yAxisLabel: "% Of Session In Behavior",
  },
  "% Trials Meeting Duration Criterion": {
    kind: "percentage-line",
    title: "% Trials Meeting Duration Criterion",
    field: "percent_trials_meeting_duration_criterion",
    chartTitle: "Trials/opportunities meeting the duration criterion",
    yAxisLabel: "% Meeting Criterion",
  },
};

/**
 * Resolves the chart config for a selected metric tab.
 *
 * @param metricTitle - The toggle option `label` (canonical metric title).
 * @returns The matching config, or `null` when the metric has no chart yet
 *          (caller should render an empty/unsupported state).
 */
export function getMetricChartConfig(
  metricTitle: string | null | undefined
): MetricChartConfig | null {
  if (!metricTitle) return null;
  return METRIC_CHART_REGISTRY[metricTitle] ?? null;
}

/**
 * Parses a `session_date`'s calendar-date portion (`YYYY-MM-DD`) as a
 * local-midnight `Date`, ignoring any time-of-day/timezone offset on the raw
 * string. Session dates are calendar days, not instants — reading them via
 * `new Date(session_date)` lets the viewer's local timezone shift the
 * displayed day (e.g. a `...T00:00:00Z` timestamp renders as the prior day in
 * negative UTC offsets). Returns `null` when `sessionDate` is missing or its
 * date portion doesn't parse.
 */
export function parseSessionDate(sessionDate: string | null | undefined): Date | null {
  if (!sessionDate) return null;
  const [y, m, d] = sessionDate.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  const date = new Date(y, m - 1, d);
  // new Date(y, m - 1, d) normalizes out-of-range values (e.g. Feb 30 rolls
  // into March 1) instead of failing, so round-trip it to catch that case.
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;
  return date;
}

/**
 * Formats a session's date for the x-axis, e.g. "03/29/24". Falls back to
 * "Session {n}" when `session_date` is missing or unparseable — some payors
 * require dates on the axis, but a session can still lack a recorded date.
 */
function formatSessionDateLabel(metric: SessionMetricPoint): string {
  const date = parseSessionDate(metric.session_date);
  if (date) return format(date, "MM/dd/yy");
  return `Session ${metric.session_number}`;
}

/**
 * Filter-side twin of `buildLineSeries`'s value resolution: true when a session
 * has a plottable value for the config's metric, including the zero-occurrence
 * rate and opportunity-frequency special cases below.
 */
export function hasLineMetricValue(metric: SessionMetricPoint, config: LineChartConfig): boolean {
  const value = metric[config.field];
  if (typeof value === "number" && Number.isFinite(value)) return true;

  // Keep sessions with valid zero occurrences on rate and opportunity-frequency charts.
  // These sessions contain data (`count_per_session = 0`) but may omit
  // the derived chart field; they should render as 0, not be filtered out.
  if (
    (config.field === "rate_per_hour" || config.field === "opportunity_frequency") &&
    metric.count_per_session === 0
  ) {
    return true;
  }

  return false;
}

/**
 * Shapes a target's session metrics into the parallel `labels` / `dateLabels`
 * / `data` arrays a line chart expects. Sessions with no value for `field`
 * (e.g. a session with no data points) become `null` so the chart renders a
 * gap.
 *
 * Shared by every line-chart category (percentage, count/rate, duration) — only
 * the y-axis scaling and an optional value `transform` differ, both driven by
 * the metric's config.
 *
 * @param metrics - Oldest-first session metric points from `session-metrics`.
 * @param field - The numeric `SessionMetricPoint` field to read per session.
 * @param transform - Optional unit conversion applied to non-null values
 *                    (e.g. ms → seconds for duration metrics).
 */
export function buildLineSeries(
  metrics: SessionMetricPoint[],
  field: LineMetricField,
  transform?: (value: number) => number
): { labels: string[]; dateLabels: string[]; data: (number | null)[] } {
  const resolveValue = (metric: SessionMetricPoint): number | null => {
    const value = metric[field];
    if (typeof value === "number" && Number.isFinite(value)) return value;

    // Some occurrence sessions with valid `count_per_session = 0` can omit a
    // derived count/rate field. Treat them as a plotted 0 while still excluding
    // sessions with no data at all.
    if (
      (field === "rate_per_hour" || field === "opportunity_frequency") &&
      metric.count_per_session === 0
    ) {
      return 0;
    }

    return null;
  };

  return {
    labels: metrics.map((m) => String(m.session_number)),
    dateLabels: metrics.map(formatSessionDateLabel),
    data: metrics.map((m) => {
      const value = resolveValue(m);
      if (value == null) return null;
      return transform ? transform(value) : value;
    }),
  };
}

/**
 * Formats a plotted value for the chart's hover tooltip, e.g. "58.7% Independent"
 * or "2.5s Mean Duration". Whole numbers stay integers; fractional values show a
 * single decimal. The unit and metric noun are derived from the config `kind`
 * and `title`:
 *
 * - `percentage-line` → `"{n}% {title-without-leading-%}"` ("58.7% Independent").
 * - `duration-line` / `latency-line` → `"{n}s {title}"` (values are seconds).
 * - `count-rate-line` / `rating-line` → `"{n} {title}"` (no unit).
 *
 * `config.title` is the canonical metric title, which matches the active toggle
 * option label shown in the picker.
 */
export function formatMetricValueLabel(value: number, config: LineChartConfig): string {
  const num = Number.isInteger(value) ? String(value) : value.toFixed(1);

  if (config.kind === "percentage-line") {
    // The title already carries a leading "% " (e.g. "% Independent"); drop it
    // so the unit isn't duplicated ("58.7% Independent", not "58.7% % Independent").
    const noun = config.title.replace(/^%\s*/, "");
    return `${num}% ${noun}`.trim();
  }

  if (config.kind === "duration-line" || config.kind === "latency-line") {
    return `${num}s ${config.title}`;
  }

  return `${num} ${config.title}`;
}
